import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { HiOutlineChartBar, HiOutlineClock, HiOutlineCollection } from 'react-icons/hi';
import StatCard from '../../components/cards/StatCard';
import { supabase } from '../../utils/supabase';
import { isReservedFieldName } from '../../utils/config';
import { formatNaira, getCurrencyTotals, isCurrencyField } from '../../utils/currency';

function Summary({ section, selectedTerm }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fields = useMemo(
    () => (section?.fields || []).filter(field => !isReservedFieldName(field)),
    [section]
  );

  const fetchRecords = useCallback(async () => {
    if (!section?.id || !selectedTerm?.id) {
      setRecords([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: recordsError } = await supabase
        .from('records')
        .select('id, record_code, record_id, data, created_at')
        .eq('section_id', section.id)
        .eq('term_id', selectedTerm.id)
        .order('created_at', { ascending: false });

      if (recordsError) throw recordsError;

      setRecords(data || []);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Could not load summary.');
    } finally {
      setLoading(false);
    }
  }, [section?.id, selectedTerm?.id]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    return records.filter(record => {
      if (!record.created_at) return false;
      const createdAt = new Date(record.created_at);
      return (
        createdAt.getFullYear() === now.getFullYear()
        && createdAt.getMonth() === now.getMonth()
      );
    }).length;
  }, [records]);

  const currencyTotals = useMemo(() => getCurrencyTotals(records, fields), [records, fields]);
  const recentRecords = records.slice(0, 5);

  if (loading) {
    return (
      <div className="rounded-xl border border-gray-100 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map(item => (
            <div key={item} className="h-28 animate-pulse rounded-xl bg-gray-100 dark:bg-neutral-800" />
          ))}
        </div>
        <div className="mt-5 h-56 animate-pulse rounded-xl bg-gray-100 dark:bg-neutral-800" />
        <p className="mt-4 text-center text-sm font-medium text-gray-400 dark:text-neutral-500">Loading summary...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:border-red-800 dark:bg-neutral-900 dark:text-red-300">
          Could not load summary: {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        <StatCard
          label="Total Records"
          value={records.length}
          helper={`Saved under ${section.name}`}
          icon={<HiOutlineCollection className="text-xl" />}
        />
        <StatCard
          label="This Month"
          value={thisMonthCount}
          helper="Created in the current month"
          icon={<HiOutlineClock className="text-xl" />}
        />
        <StatCard
          label="Fields"
          value={fields.length}
          helper="User-created editable fields"
          icon={<HiOutlineChartBar className="text-xl" />}
        />
      </div>

      {currencyTotals.length > 0 && (
        <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Money Totals</h2>
            <p className="text-sm text-gray-500 dark:text-neutral-400">
              Auto-calculated from amount-like fields in this section.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {currencyTotals.map(item => (
              <div
                key={item.field}
                className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950"
              >
                <p className="text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-neutral-400">
                  Total {item.field}
                </p>
                <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-neutral-100">
                  {formatNaira(item.total)}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-5">
        <section className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
          <h2 className="text-lg font-bold text-gray-900 dark:text-neutral-100">Recent Entries</h2>
          <div className="mt-5 space-y-3">
            {recentRecords.length === 0 ? (
              <EmptyState title="No recent entries" text="Records saved from Add New will show here." />
            ) : (
              recentRecords.map(record => (
                <div
                  key={record.id}
                  className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-4 py-3 dark:border-neutral-800"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-gray-900 dark:text-neutral-100">
                      {record.record_code || record.record_id}
                    </p>
                    <p className="truncate text-xs text-gray-400">
                      {getRecordPreview(record, fields)}
                    </p>
                  </div>
                  <p className="shrink-0 text-xs font-medium text-gray-400">
                    {formatShortDate(record.created_at)}
                  </p>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function getRecordPreview(record, fields) {
  const firstFieldWithValue = fields.find(field => record.data?.[field]);
  if (!firstFieldWithValue) {
    return 'No field values';
  }

  const value = isCurrencyField(firstFieldWithValue)
    ? formatNaira(record.data[firstFieldWithValue])
    : record.data[firstFieldWithValue];

  return `${firstFieldWithValue}: ${value}`;
}

function formatShortDate(value) {
  if (!value) return '-';

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function EmptyState({ title, text }) {
  return (
    <div className="rounded-xl border border-dashed border-gray-200 px-5 py-8 text-center dark:border-neutral-800">
      <p className="font-bold text-gray-900 dark:text-neutral-100">{title}</p>
      <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">{text}</p>
    </div>
  );
}

export default Summary;
